/**
 * Adaptateur « sorts » — COMMENTS v0.4.1 §1.4.
 *
 * Deux familles depuis la v0.5.0 :
 *  - les **sorts d'emplacement** (`src/data/slotSpells.ts`), débloqués avec les
 *    emplacements de dés Maîtres (Paire, Yeux du serpent à 2 ; Brelan, 666 à 3) ;
 *  - les **sorts de dés** (`src/data/dieSpells.ts`), portés par les dés Maîtres
 *    (`MasterDie.spells`) et tirés à l'achat selon la rareté.
 *
 * v0.7.0 (COMMENTS v0.6.0 §1.2) : les emplacements 4 et 5 apportent *Double
 * paire*, *Carré*, *Suite* et *Full* ; **666 quitte les sorts d'emplacement**
 * pour devenir un sort de dé (`sixes`), aux côtés de *Premiers*. Le catalogue
 * de repli ci-dessous suit exactement ce partage — il ne sert que tant que
 * `src/data/*Spells.ts` n'a pas livré les nouvelles entrées.
 *
 * Ces deux modules sont livrés **en parallèle** par l'agent `src/core` : ils ne
 * peuvent donc pas être importés statiquement (le build casserait tant qu'ils
 * n'existent pas). Ils sont résolus par `import.meta.glob(..., eager)`, qui
 * n'inclut que les fichiers réellement présents au build, et reconnus **par
 * leur forme** (`requiredSlots` pour un sort d'emplacement, `condition`/`rare`
 * pour un sort de dé) plutôt que par le nom de leur export.
 *
 * Sans eux, un catalogue de repli reprend les tableaux de COMMENTS §1.4 : les
 * écrans affichent la même chose, seuls les identifiants du cœur manquent.
 */

import type { Game } from '../core/game';
import { nodeNameOf } from './abyssApi';
import { callGame, hasMethod, nodeLevel } from './gameApi';

export interface SlotSpellDef {
  id: string;
  name: string;
  description: string;
  /** Condition de déclenchement, en clair (`trigger` côté données). */
  condition: string;
  /** Emplacements de dés Maîtres nécessaires (`slots` côté données). */
  requiredSlots: number;
  /**
   * A95 : nœud de l'arbre dont ce sort dépend (les cinq sorts de Poker :
   * `aby_poker`). Absent pour les sorts qui ne tiennent qu'aux emplacements.
   */
  requiresNode?: string;
}

export interface DieSpellDef {
  id: string;
  name: string;
  description: string;
  /** Condition de déclenchement, en clair (COMMENTS §1.4). */
  condition: string;
  rare: boolean;
  /**
   * A98 : nombre de faces minimal des dés qui peuvent porter le sort (Vingt-dicte,
   * Null et Tempête : 12 ; Rouleau Compresseur : 20). Absent : tous les dés.
   */
  minSides?: number;
}

/** Un sort déclenché par un lancer (`ThrowResult.spells`). */
export interface TriggeredSpell {
  id: string;
  kind: 'slot' | 'die';
  multiplier: number;
  bonusSeconds: number;
  goldMult: number;
  label: string;
  /** A89 : sort de dégâts renforcé par *Arrière-garde* (porteur dans les deux derniers emplacements). */
  rearguard?: boolean;
  /** Multiplicateur du sort **avant** *Arrière-garde* (`multiplier` est le multiplicateur final). */
  baseMultiplier?: number;
  /**
   * A100-2 : multiplicateur du sort **avant** *Préparation* (`multiplier` est le final) ;
   * présent seulement si le cœur A100 publie la trace — sort de dé d'un dé Maître des
   * emplacements 1 à 3.
   */
  prepFrom?: number;
}

// ─────────────────────────────────────────────────────────── Catalogue de repli

/** Clé de comparaison : minuscules, sans accent ni séparateur (« Yeux du serpent » → « yeuxduserpent »). */
function key(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

const FALLBACK_SLOT: SlotSpellDef[] = [
  {
    id: 'pair',
    name: 'Paire',
    description: 'Multiplie les dégâts du lancer par 1,25.',
    condition: 'Deux dés Maîtres affichent la même face',
    requiredSlots: 2,
  },
  {
    id: 'snakeEyes',
    name: 'Yeux du serpent',
    description: 'Si tous vos dés affichent une face minimale, multiplie les dégâts du lancer par 0,75.',
    condition: 'Tous les dés Maîtres affichent leur face minimale',
    requiredSlots: 2,
  },
  {
    id: 'triple',
    name: 'Brelan',
    description: 'Multiplie les dégâts du lancer par 2.',
    condition: 'Trois dés Maîtres affichent la même face',
    requiredSlots: 3,
  },
  {
    id: 'doublePair',
    name: 'Double paire',
    description: 'Multiplie les dégâts du lancer par 2,5.',
    condition: 'Deux couples de dés Maîtres affichent la même face',
    requiredSlots: 4,
  },
  {
    id: 'four',
    name: 'Carré',
    description: 'Multiplie les dégâts du lancer par 4.',
    condition: 'Quatre dés Maîtres affichent la même face',
    requiredSlots: 4,
  },
  {
    id: 'straight',
    name: 'Suite',
    description: 'Multiplie les dégâts du lancer par la plus haute face de la suite.',
    condition: 'Cinq dés Maîtres affichent des faces consécutives',
    requiredSlots: 5,
  },
  {
    id: 'full',
    name: 'Full',
    description: 'Multiplie les dégâts du lancer par le produit du brelan et de la paire.',
    condition: 'Un brelan et une paire en même temps',
    requiredSlots: 5,
  },
];

/**
 * Texte de *42* (A98 : plus de secondes — les deux dés qui ont montré le 4 et
 * le 2 sont lancés une fois de plus au lancer suivant). Les données du cœur
 * priment ; ce texte ne sert qu'au catalogue de repli.
 */
const FORTY_TWO_TEXT =
  'Au prochain lancer, les deux dés qui ont fait le 4 et le 2 sont lancés une fois de plus.';

const FALLBACK_DIE: DieSpellDef[] = [
  {
    id: '42',
    name: '42',
    description: FORTY_TWO_TEXT,
    condition: 'Une face 4 et une face 2',
    rare: false,
  },
  // A98 (COMMENTS v0.48.1 §1.3.2) : les quatre nouveaux sorts, réservés aux gros dés.
  {
    id: 'twentyDict',
    name: 'Vingt-dicte',
    description: 'Ce lancer lance le double de dés Basiques.',
    condition: 'Au moins 4 faces Maîtres strictement supérieures à 6',
    rare: false,
    minSides: 12,
  },
  {
    id: 'nullMark',
    name: 'Null',
    description:
      'Au premier déclenchement sur un monstre, pose une marque qui retient les dégâts des lancers suivants. ' +
      'Au second, le lancer inflige son total plus tout ce qui a été retenu, et la marque disparaît.',
    condition: 'Aucune face Maître en double',
    rare: false,
    minSides: 12,
  },
  {
    id: 'storm',
    name: 'Tempête',
    description:
      'Une tempête fait rage 5 secondes : votre temps de retour est réduit de 25 %. Réappliquer Tempête rafraîchit la durée.',
    condition: '3 faces Maîtres strictement supérieures à 10',
    rare: false,
    minSides: 12,
  },
  {
    id: 'steamroller',
    name: 'Rouleau Compresseur',
    description: 'Multiplie les dégâts du lancer par le produit de la plus petite face et de la plus haute face.',
    condition: 'La plus haute face de ce lancer est supérieure ou égale à celle du lancer précédent',
    rare: true,
    minSides: 20,
  },
  {
    id: 'parity',
    name: 'Parité',
    description: 'Multiplie les dégâts du lancer par 1,5.',
    condition: 'Toutes les faces paires ou toutes impaires',
    rare: false,
  },
  {
    id: 'pi',
    name: 'Pi',
    description: 'Multiplie les dégâts du lancer par 3,14.',
    condition: 'Les faces 3, 1 et 4',
    rare: true,
  },
  {
    id: 'maxroll',
    name: 'Maxroll',
    description: 'Multiplie les dégâts du lancer par 1,75, doublé par emplacement au-delà de 2.',
    condition: 'Tous les dés affichent une face maximale',
    rare: true,
  },
  {
    id: 'goldenNumber',
    name: "Nombre d'or",
    description: "Multiplie l'or gagné à la mort du monstre par 1,61.",
    condition: 'Les faces 1, 6, 1 et 8',
    rare: true,
  },
  {
    id: 'primes',
    name: 'Premiers',
    description: 'Multiplie les dégâts des 3 prochains lancers par 2, puis 3, puis 5.',
    condition: 'Un nombre premier de dés Maîtres affiche une face première',
    rare: true,
  },
  {
    id: 'sixes',
    name: '666',
    description:
      "Le sol prend les flammes de l'ENFER jusqu'à la fin de l'expédition : les dégâts des lancers sont multipliés par 1,5.",
    condition: 'Les faces 6, 6 et 6',
    rare: true,
  },
];

/**
 * Catalogue de repli, exposé pour les tests : le texte de *Premiers* n'y liste
 * aucune face en dur (la table des premiers monte à 97 depuis le d100, A89).
 */
export function fallbackCatalog(): { slot: readonly SlotSpellDef[]; die: readonly DieSpellDef[] } {
  return { slot: FALLBACK_SLOT, die: FALLBACK_DIE };
}

/** Noms historiques (v0.4.x) : gardés pour les journaux et sauvegardes anciens. */
const LEGACY_NAME: Record<string, string> = {
  sixes: '666',
  '666': '666',
  spark: 'Étincelle',
  snakeeyes: 'Yeux du serpent',
  arc: 'Arc électrique',
  lifesense: 'Sens de la vie',
  pair: 'Paire',
  paire: 'Paire',
  triple: 'Brelan',
  brelan: 'Brelan',
};

// ──────────────────────────────────────────────────── Résolution des modules

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Toutes les valeurs « plates » exportées par les modules de `src/data`. */
function dataCandidates(): unknown[] {
  const out: unknown[] = [];
  let modules: Record<string, unknown> = {};
  try {
    modules = import.meta.glob('../data/*.ts', { eager: true }) as Record<string, unknown>;
  } catch {
    return out;
  }
  for (const mod of Object.values(modules)) {
    if (!isRecord(mod)) continue;
    for (const value of Object.values(mod)) {
      if (Array.isArray(value)) out.push(...value);
      else if (isRecord(value)) out.push(...Object.values(value));
    }
  }
  return out;
}

let slotCache: SlotSpellDef[] | null = null;
let dieCache: DieSpellDef[] | null = null;

function scan(): void {
  if (slotCache && dieCache) return;
  const slots: SlotSpellDef[] = [];
  const dice: DieSpellDef[] = [];
  const seenSlot = new Set<string>();
  const seenDie = new Set<string>();
  for (const value of dataCandidates()) {
    if (!isRecord(value)) continue;
    const id = str(value.id);
    if (!id || typeof value.name !== 'string') continue;
    const required = typeof value.requiredSlots === 'number' ? value.requiredSlots : value.slots;
    const condition = str(value.condition, str(value.trigger, '—'));
    const description = str(value.description, str(value.effect));
    const isDie = typeof value.pool === 'string' || typeof value.rare === 'boolean';
    if (typeof required === 'number' && !isDie) {
      if (seenSlot.has(id)) continue;
      seenSlot.add(id);
      slots.push({
        id,
        name: value.name,
        description,
        condition,
        requiredSlots: num(required, 2),
        ...(typeof value.requiresNode === 'string' && value.requiresNode ? { requiresNode: value.requiresNode } : {}),
      });
    } else if (isDie || condition !== '—') {
      if (seenDie.has(id)) continue;
      seenDie.add(id);
      dice.push({
        id,
        name: value.name,
        description,
        condition,
        rare: value.pool === 'rare' || value.rare === true,
        ...(typeof value.minSides === 'number' && value.minSides > 1 ? { minSides: Math.floor(value.minSides) } : {}),
      });
    }
  }
  slotCache = slots.length > 0 ? slots.sort((a, b) => a.requiredSlots - b.requiredSlots) : FALLBACK_SLOT;
  dieCache = dice.length > 0 ? dice : FALLBACK_DIE;
}

/** Sorts d'emplacement, triés par nombre d'emplacements requis. */
export function slotSpells(): SlotSpellDef[] {
  scan();
  return slotCache ?? FALLBACK_SLOT;
}

/** Sorts de dés, communs d'abord. */
export function dieSpells(): DieSpellDef[] {
  scan();
  const list = dieCache ?? FALLBACK_DIE;
  return list.slice().sort((a, b) => Number(a.rare) - Number(b.rare));
}

export function dieSpellById(id: string): DieSpellDef | null {
  const match = (s: DieSpellDef): boolean => s.id === id || key(s.id) === key(id);
  // A98 : un sort que les données du cœur ne livrent pas encore garde son texte de repli.
  return dieSpells().find(match) ?? FALLBACK_DIE.find(match) ?? null;
}

export function slotSpellById(id: string): SlotSpellDef | null {
  return slotSpells().find((s) => s.id === id || key(s.id) === key(id)) ?? null;
}

/**
 * Libellé lisible d'un identifiant de sort, quelle que soit sa famille.
 * Repli : nom historique v0.4.x, puis l'identifiant brut (jamais vide).
 */
export function spellLabel(id: string): string {
  if (!id) return '—';
  return (
    dieSpellById(id)?.name ?? slotSpellById(id)?.name ?? LEGACY_NAME[key(id)] ?? id
  );
}

// ─────────────────────── Textes, verrous et Grimoires (v0.47.0, A95, lot B)

/** Les deux Grimoires de l'Abîme (`data/nodes.ts`) : leurs niveaux renforcent les sorts. */
export const GRIMOIRE_NUMBERS_NODE = 'aby_grimoire_numbers';
export const GRIMOIRE_FACES_NODE = 'aby_grimoire_faces';

/** Condition et effet d'un sort, tels qu'on les affiche. */
export interface SpellTexts {
  condition: string;
  description: string;
}

/**
 * Textes d'un sort **avec les Grimoires appliqués** : `game.spellDescription(id)`
 * (`{ condition, effect }`, conditions élargies au niveau 3, multiplicateurs
 * renforcés) quand le cœur la fournit, sinon ceux des données — jamais vide.
 */
export function spellTexts(
  game: Game | null | undefined,
  def: { id: string; condition: string; description: string },
): SpellTexts {
  const base = { condition: def.condition, description: def.description };
  if (!game || !hasMethod(game, 'spellDescription')) return base;
  const out = callGame(game, 'spellDescription', def.id);
  if (!isRecord(out)) return base;
  const condition = str(out.condition);
  const effect = str(out.effect, str(out.description));
  return { condition: condition || base.condition, description: effect || base.description };
}

/** État d'un sort d'emplacement : débloqué, ou le nom du nœud qui lui manque. */
export interface SlotSpellState {
  unlocked: boolean;
  /** Nom du nœud manquant (« Qui a dit Poker ? »), `null` si le verrou n'est pas un nœud. */
  missingNode: string | null;
}

/**
 * Un sort d'emplacement est **verrouillé** tant que son nœud (`requiresNode`)
 * manque ; ensuite il suit `game.slotSpellsUnlocked()` (Poker compris, selon
 * les emplacements) et, sans cette méthode, le nombre d'emplacements.
 */
export function slotSpellState(game: Game, def: SlotSpellDef, slots: number): SlotSpellState {
  if (def.requiresNode && nodeLevel(game, def.requiresNode) < 1) {
    return { unlocked: false, missingNode: nodeNameOf(def.requiresNode) };
  }
  const list = callGame(game, 'slotSpellsUnlocked');
  if (Array.isArray(list)) return { unlocked: list.includes(def.id), missingNode: null };
  return { unlocked: slots >= def.requiredSlots, missingNode: null };
}

/** Niveaux des deux Grimoires : `game.grimoireLevels()`, sinon le niveau des nœuds. */
export function grimoireLevelsOf(game: Game): { numbers: number; faces: number } {
  const out = callGame(game, 'grimoireLevels');
  const read = (v: unknown, nodeId: string): number => {
    const n = typeof v === 'number' && Number.isFinite(v) ? Math.floor(v) : nodeLevel(game, nodeId);
    return Math.max(0, n);
  };
  const o = isRecord(out) ? out : {};
  return { numbers: read(o.numbers, GRIMOIRE_NUMBERS_NODE), faces: read(o.faces, GRIMOIRE_FACES_NODE) };
}

/** « Grimoire des nombres · niveau 3 » ; `null` au niveau 0 (la ligne n'existe pas). */
export function grimoireLine(nodeId: string, level: number): string | null {
  return level >= 1 ? `${nodeNameOf(nodeId)} · niveau ${Math.floor(level)}` : null;
}

/**
 * Dés qui peuvent porter un sort (A98) : « d12 et d20 » pour un plancher de
 * 12 faces, « d20 » pour 20 ; `null` quand le sort va à tous les dés.
 */
export function spellSidesText(def: { minSides?: number }): string | null {
  const min = def.minSides;
  if (typeof min !== 'number' || !Number.isFinite(min) || min <= 6) return null;
  return min > 12 ? 'd20' : 'd12 et d20';
}

// ──────────────────────────────────────────────────────────── Sorts d'un dé

/** Identifiants de sorts portés par un dé Maître (`MasterDie.spells`). */
export function dieSpellIds(die: unknown): string[] {
  const raw = isRecord(die) ? die.spells : undefined;
  if (!Array.isArray(raw)) return [];
  return raw.filter((s): s is string => typeof s === 'string' && s.length > 0);
}

/**
 * Définitions des sorts d'un dé. Un identifiant inconnu du catalogue produit
 * quand même une entrée (nom = identifiant) : mieux vaut un libellé brut qu'un
 * sort invisible.
 */
export function dieSpellsOf(die: unknown): DieSpellDef[] {
  return dieSpellIds(die).map(
    (id) =>
      dieSpellById(id) ?? {
        id,
        name: spellLabel(id),
        description: '',
        condition: '—',
        rare: false,
      },
  );
}

// ───────────────────────────────────────────────── Sorts déclenchés (lancer)

/**
 * Sorts déclenchés par un lancer : `ThrowResult.spells` (v0.5.0) avec repli sur
 * `ThrowResult.spell` (v0.4.x, un seul sort d'emplacement).
 */
export function triggeredSpells(result: unknown): TriggeredSpell[] {
  if (!isRecord(result)) return [];
  const list = result.spells;
  if (Array.isArray(list)) {
    const out: TriggeredSpell[] = [];
    for (const raw of list) {
      if (!isRecord(raw)) continue;
      const id = str(raw.id);
      if (!id) continue;
      out.push(
        withPrep(
          withRearguard(
            {
              id,
              kind: raw.kind === 'die' ? 'die' : 'slot',
              multiplier: num(raw.multiplier, 1),
              bonusSeconds: num(raw.bonusSeconds),
              goldMult: num(raw.goldMult, 1),
              label: str(raw.label) || spellLabel(id),
            },
            raw,
          ),
          raw,
        ),
      );
    }
    return out;
  }
  const legacy = result.spell;
  if (!isRecord(legacy)) return [];
  const id = str(legacy.id);
  if (!id) return [];
  return [
    {
      id,
      kind: 'slot',
      multiplier: num(legacy.baseMultiplier, num(legacy.multiplier, 1)),
      bonusSeconds: num(legacy.bonusSeconds),
      goldMult: 1,
      label: spellLabel(id),
    },
  ];
}

/**
 * *Arrière-garde* (A89) : marque un sort déclenché renforcé, **sans casser**
 * tant que le cœur n'a pas livré. Champs lus : `rearguard: true`, le
 * multiplicateur de base `m` (ou `baseMultiplier`) et le renforcé `m'`
 * (`mPrime`, `boosted`…) ; sans `m'`, `multiplier` est déjà le renforcé.
 */
export function withRearguard(spell: TriggeredSpell, raw: Record<string, unknown>): TriggeredSpell {
  if (raw.rearguard !== true) return spell;
  const pick = (...keys: string[]): number | undefined => {
    for (const k of keys) if (typeof raw[k] === 'number' && Number.isFinite(raw[k])) return raw[k] as number;
    return undefined;
  };
  const base = pick('m', 'baseMultiplier', 'baseMult');
  const boosted = pick('mPrime', "m'", 'boosted', 'boostedMultiplier');
  return {
    ...spell,
    rearguard: true,
    ...(boosted !== undefined ? { multiplier: boosted } : {}),
    ...(base !== undefined ? { baseMultiplier: base } : {}),
  };
}

/**
 * *Préparation* (A100-2) : marque un sort de dé renforcé par le talent, **sans
 * casser** tant que le cœur n'a pas livré. Champ lu : `prepFrom`, le
 * multiplicateur du sort avant le renfort ; absent, le sort reste tel quel.
 */
export function withPrep(spell: TriggeredSpell, raw: Record<string, unknown>): TriggeredSpell {
  const from = raw.prepFrom;
  if (typeof from !== 'number' || !Number.isFinite(from)) return spell;
  return { ...spell, prepFrom: from };
}

/** « Paire ×1,25 », « Nombre d'or or ×1,61 » — résumé d'un sort déclenché. */
export function triggeredSpellText(spell: TriggeredSpell): string {
  const parts: string[] = [];
  if (spell.multiplier !== 1) {
    // A89 / A100-2 : un sort renforcé (Arrière-garde, Préparation) dit d'où il part et où il arrive.
    const start =
      spell.rearguard && spell.baseMultiplier !== undefined
        ? spell.baseMultiplier
        : spell.prepFrom !== undefined
          ? spell.prepFrom
          : undefined;
    const from = start !== undefined && start !== spell.multiplier;
    parts.push(from ? `×${fr(start as number)} → ×${fr(spell.multiplier)}` : `×${fr(spell.multiplier)}`);
  }
  if (spell.bonusSeconds !== 0) parts.push(`${spell.bonusSeconds > 0 ? '+' : ''}${fr(spell.bonusSeconds, 1)} s`);
  if (spell.goldMult !== 1) parts.push(`or ×${fr(spell.goldMult)}`);
  const boosts = [spell.rearguard ? 'Arrière-garde' : '', spell.prepFrom !== undefined ? 'Préparation' : ''].filter(Boolean);
  if (boosts.length > 0) parts.push(`(${boosts.join(', ')})`);
  return parts.length > 0 ? `${spell.label} ${parts.join(' ')}` : spell.label;
}

/** Nombre français court (virgule décimale, zéros de fin retirés). */
function fr(n: number, decimals = 2): string {
  if (!Number.isFinite(n)) return '—';
  const fixed = n.toFixed(decimals);
  const trimmed = fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
  return trimmed.replace('.', ',');
}
